import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {
  getCurrentRepositoryNwo,
  getNavigationTabCountAuthoritativeVersion,
  getNavigationTabCountSnapshot,
  getNavigationTabCountTargetVersion,
  updateNavigationTabCount,
} from '@github-ui/global-navigation'
import {useMutation} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {md2html} from '@github-ui/md2html'
import {usePullRequestBasePath} from '../stores/selectors'
import {useLayoutBaseRepository} from '../stores/layout-selectors'
import {
  conversationCommentsQueryKey,
  removeCommentQueryData,
  resolvePendingConversationComment,
  upsertConversationItemQueryData,
} from '../page-data/loaders/use-conversation-comments'
import {registerIssueCommentKey} from '../components/comments/utils/pending-comment-item-keys'
import {getCurrentUser} from '../components/comments/utils/get-author-filters'
import type {OrgBlockable, PersonalBlockable, Reactable} from '../page-data/payloads/comment-interfaces'
import type {CommentAuthorUserType} from '@github-ui/commenting/ActivityHeader'
import type {CommentAuthorAssociation} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {ConversationIssueComment, Repository} from '../page-data/payloads/conversation-comments'
import type {HIDE_OPTIONS} from '@github-ui/commenting/HideCommentActions'

export type CreateTimelineCommentMutationVariables =
  | {text: string; closePullRequest?: never; reopenPullRequest?: never}
  | {text: string; closePullRequest: true; reopenPullRequest?: never}
  | {text: string; closePullRequest?: never; reopenPullRequest: true}

type MutationResponse = {
  message: string
  commentId: number
  issueComment: IssueComment
}

export function useCreatePullRequestTimelineCommentMutation() {
  const basePath = usePullRequestBasePath()
  const repository = useLayoutBaseRepository()
  const apiUrl = `${basePath}/page_data/${PageData.submitPullRequestTimelineComment}`

  return useMutation({
    mutationFn: async (variables: CreateTimelineCommentMutationVariables) => {
      const result = await reactFetchJSON(`${apiUrl}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: variables,
      })

      const json = (await parseJSONWithBetterErrors(result)) as MutationResponse
      throwErrorsIfBadResponse(result, json)
      return {
        ...json,
        issueComment: buildConversationIssueComment(json.issueComment),
      }
    },
    onMutate: (variables, {client}) => {
      const currentUser = getCurrentUser()

      let pendingKey: string | undefined
      let pendingCommentId: number | undefined
      if (currentUser) {
        const optimisticComment = buildOptimisticIssueComment({text: variables.text, currentUser, repository})
        pendingCommentId = optimisticComment.id
        pendingKey = `pending-issue-comment-${pendingCommentId}`
        // Register the optimistic comment's temporary ID against the key it should keep so it doesn't remount once
        // its ID is later swapped for the real one below.
        registerIssueCommentKey(pendingCommentId, pendingKey)
        upsertConversationItemQueryData(client, basePath, optimisticComment)
      }

      return {
        pendingKey,
        pendingCommentId,
        repositoryNwo: getCurrentRepositoryNwo(),
        tabCount: getNavigationTabCountSnapshot('pull-requests'),
        targetVersion: getNavigationTabCountTargetVersion('pull-requests'),
        updateVersion: getNavigationTabCountAuthoritativeVersion('pull-requests'),
      }
    },
    onSuccess: (
      data,
      inputs,
      {pendingKey, pendingCommentId, repositoryNwo, tabCount, targetVersion, updateVersion},
      context,
    ) => {
      if (pendingKey !== undefined && pendingCommentId !== undefined) {
        // Register the same key against the comment's real ID so replacing it below - and any later refetch of
        // the same comment - doesn't remount it, then swap the placeholder for the confirmed comment in place.
        registerIssueCommentKey(data.issueComment.id, pendingKey)
        resolvePendingConversationComment(context.client, basePath, pendingCommentId, data.issueComment)
      } else {
        upsertConversationItemQueryData(context.client, basePath, data.issueComment)
      }
      // buildConversationIssueComment has to fill in the blanks for unknown data, so we still need to refetch the
      // conversation to get an accurate comment. We can remove this once we migrate to a conversation-specific API
      // where the full data is available, which will allow us to avoid refetching the whole conversation.
      // FIXME: investigate returning a ConversationIssueComment from create_timeline_comment, or fetching the
      // created comment from the conversation API, before removing this adapter and the full refetch.
      context.client.invalidateQueries({queryKey: conversationCommentsQueryKey(basePath), exact: true})

      if (inputs.closePullRequest) {
        updateNavigationTabCount(
          'pull-requests',
          {
            type: 'delta',
            delta: -1,
            ...(tabCount?.exact ? {expectedCountAfterAuthoritativeUpdate: tabCount.count} : {}),
          },
          {
            repositoryNwo,
            expectedUpdateVersion: updateVersion,
            expectedTargetVersion: targetVersion,
          },
        )
      } else if (inputs.reopenPullRequest) {
        updateNavigationTabCount(
          'pull-requests',
          {
            type: 'delta',
            delta: 1,
            ...(tabCount?.exact ? {expectedCountAfterAuthoritativeUpdate: tabCount.count} : {}),
          },
          {
            repositoryNwo,
            expectedUpdateVersion: updateVersion,
            expectedTargetVersion: targetVersion,
          },
        )
      }
    },
    onError: (_error, _inputs, context, {client}) => {
      if (context?.pendingCommentId === undefined) return

      // Roll back the optimistic comment; the composer is responsible for restoring the draft text.
      removeCommentQueryData(client, basePath, context.pendingCommentId)
    },
  })
}

/**
 * Builds a client-only issue comment to render optimistically while its creation mutation is in flight. Every
 * viewer-can-* flag is `false` so the comment renders as read-only until the real comment (with its actual
 * permissions) replaces it.
 */
function buildOptimisticIssueComment({
  text,
  currentUser,
  repository,
}: {
  text: string
  currentUser: {login: string; avatarUrl: string; isAgent?: boolean}
  repository: ReturnType<typeof useLayoutBaseRepository>
}): ConversationIssueComment {
  const now = new Date().toISOString()
  // use a negative timestamp as a temporary unique ID for the optimistic comment.
  const id = -Date.now()

  return {
    type: 'issue',
    id,
    relayId: `client-pending-comment-${id}`,
    author: currentUser,
    authorAssociation: 'NONE',
    body: text,
    bodyHTML: md2html(text),
    bodyVersion: '',
    createdAt: now,
    lastUserContentEdit: null,
    publishedAt: now,
    stafftoolsUrl: null,
    isHidden: false,
    minimizedReason: null,
    reactionGroups: [],
    viewerCanBlockFromOrg: false,
    viewerCanBlockPersonally: false,
    viewerCanDelete: false,
    viewerCanMinimize: false,
    viewerCanReact: false,
    viewerCanSeeMinimizeButton: false,
    viewerCanSeeUnminimizeButton: false,
    viewerCanUnblockFromOrg: false,
    viewerCanUnblockPersonally: false,
    viewerCanUpdate: false,
    viewerDidAuthor: true,
    repository: {
      id: String(repository.id),
      isPrivate: repository.private ?? false,
      name: repository.name,
      owner: {
        id: '',
        login: repository.ownerLogin,
        url: `/${repository.ownerLogin}`,
      },
    },
    url: '',
  }
}

/**
 * Legacy issue comment model returned by the create endpoint.
 * @deprecated use the new conversation comments models instead */
export interface IssueComment extends OrgBlockable, PersonalBlockable, Reactable {
  id: string
  relayId: string
  authorAssociation: CommentAuthorAssociation
  authorAvatarUrl: string | null
  authorLogin: string
  authorSlug?: string
  userType?: CommentAuthorUserType
  body: string
  bodyHtml: SafeHTMLString
  createdAt: string
  isHidden: boolean
  minimizedReason: HIDE_OPTIONS | null
  publishedAt: string | null
  repository: Repository | null
  url: string
}

/**
 * Temporary shim mapping the legacy issue comments payload onto the conversation comments model.
 * @deprecated use conversation comments models directly
 */
function buildConversationIssueComment(comment: IssueComment): ConversationIssueComment {
  return {
    bodyVersion: '',
    lastUserContentEdit: null,
    stafftoolsUrl: null,
    viewerCanDelete: false,
    viewerCanMinimize: false,
    viewerCanSeeMinimizeButton: false,
    viewerCanSeeUnminimizeButton: false,
    viewerCanUpdate: false,
    viewerDidAuthor: false,
    type: 'issue',
    id: Number(comment.id),
    author: {
      login: comment.authorLogin,
      avatarUrl: comment.authorAvatarUrl ?? '',
      slug: comment.authorSlug,
      userType: comment.userType,
    },
    authorAssociation: comment.authorAssociation,
    body: comment.body,
    bodyHTML: comment.bodyHtml,
    createdAt: comment.createdAt,
    publishedAt: comment.publishedAt,
    isHidden: comment.isHidden,
    minimizedReason: comment.minimizedReason,
    reactionGroups: comment.reactionGroups,
    relayId: comment.relayId,
    viewerCanBlockFromOrg: comment.viewerCanBlockFromOrg,
    viewerCanBlockPersonally: comment.viewerCanBlockPersonally,
    viewerCanReact: comment.viewerCanReact,
    viewerCanUnblockFromOrg: comment.viewerCanUnblockFromOrg,
    viewerCanUnblockPersonally: comment.viewerCanUnblockPersonally,
    repository: comment.repository,
    url: comment.url,
  }
}
